import { IToast, toast } from '../features/toast'
import { Dispatch, useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { IStoreState } from '../reducers/types'
import { useTypedSelector } from './custom-hooks'
import { IToastState } from '../features/toast/store/types'
import { AnyAction } from 'redux'

export const showSingleToast = (
  toastArg: IToast,
  toastState: IToastState,
  dispatch: Dispatch<AnyAction>
) => {
  if (toastState.find((t) => t.text === toastArg.text)) {
    return
  }

  dispatch(toast.actions.showToast(toastArg))
}

export const useSingleToast = () => {
  const dispatch = useDispatch()
  const toasts = useTypedSelector((state: IStoreState) => state['@toasts'])
  const toastsRef = useRef(toasts)

  useEffect(() => {
    toastsRef.current = toasts
  }, [toasts])

  const showToast = useCallback(
    (toastArg: IToast) =>
      showSingleToast(toastArg, toastsRef.current, dispatch),
    [dispatch]
  )

  return useMemo(
    () => ({
      showToast,
    }),
    [showToast]
  )
}

export const successToast = (
  text: string,
  timeoutSeconds = 3,
  dismissible = true
) =>
  toast.actions.showToast({
    text: text,
    timeoutSeconds,
    dismissible,
    type: 'SUCCESS',
  })

export const dangerToast = (
  text = 'Det har skjedd en feil. Kunne ikke lagre.',
  timeoutSeconds = 10,
  dismissible = true
) =>
  toast.actions.showToast({
    text,
    timeoutSeconds,
    dismissible,
    type: 'DANGER',
  })

export const warningToast = (
  text: string,
  timeoutSeconds = 5,
  dismissible = true
) =>
  toast.actions.showToast({
    text,
    timeoutSeconds,
    dismissible,
    type: 'WARN',
  })

export const online = (): IToast => ({
  text: 'Applikasjonen er <b>online</b>, og alle funksjoner er tilgjengelig.',
  timeoutSeconds: 5,
  type: 'SUCCESS',
  dismissible: true,
})

export const copySaknummer = (): IToast => ({
  text: 'Saknummeret er kopiert',
  timeoutSeconds: 3,
  type: 'SUCCESS',
  dismissible: true,
})

export const offlineGeneral = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. Sjekk din internettilgang. \n' +
    '\n' +
    'Du kan starte et forberedt tilsyn og det du skriver vil bli lagret. \n' +
    '\n' +
    'Du kan ikke legge til sak, tilsynsobjekt, type tilsyn, opprette tilsynskvitteringer i arkivet, eller ferdigstille og sende ut tilsynskvitteringer før du er online. \n',
  timeoutSeconds: 15,
  type: 'WARN',
  dismissible: true,
})

export const offlineInForberedelseProcess = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b> \n' +
    '\n' +
    'Du kan derfor ikke forberede et nytt tilsyn. \n',
  type: 'DANGER',
  dismissible: true,
})

export const offlineInRegisteringProcess = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. Sjekk din internettilgang. \n' +
    '\n' +
    'Det du registrerer blir lagret, men du kan ikke opprette tilsynskvitteringen i arkivet før du er online.',
  timeoutSeconds: 10,
  type: 'WARN',
  dismissible: true,
})

export const offlineInFerdigstillingProcess = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. Du kan ikke ferdigstille og sende ut tilsynskvitteringen før applikasjonen er online. \n' +
    '\n' +
    'Prøv på nytt når du har internettilgang. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineInFerdigstillingFromHome = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. \n' +
    '\n' +
    'Du kan derfor ikke ferdigstille og sende ut tilsynskvitteringer. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineInSakAndTilsynsobjekt = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. \n' +
    '\n' +
    'Du kan derfor ikke legge til sak eller tilsynsobjekt. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineInTypeTilsynSelect = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. \n' +
    '\n' +
    'Du kan derfor ikke velge tilsynstype. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineInArkiveringSlide = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. \n' +
    '\n' +
    'Du må vente til du er online med å gå til siste side for å opprette tilsynskvitteringen i arkivet. \n',
  dismissible: true,
  type: 'DANGER',
})

export const sakITekstfelt = (): IToast => ({
  text: 'Saken må legges til eller tekstfeltet må være tomt',
  timeoutSeconds: 10,
  dismissible: true,
  type: 'DANGER',
})

export const offlineInHjemmelSelect = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. Du kan ikke velge hjemmel eller opprette tilsynskvitteringen i arkivet før applikasjonen er online\n' +
    '\n' +
    'Prøv på nytt når du har internettilgang. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineOnVelgEnkeltord = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. \n' +
    '\n' +
    'Du kan derfor ikke velge enkeltord som skal skjermes. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineOnSkjermMottaker = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. \n' +
    '\n' +
    'Du kan derfor ikke velge om mottakeren skal skjermes. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineOnUnntaOffentlighet = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. \n' +
    '\n' +
    'Du kan derfor ikke velge om dokument og/eller bilder skal unntas offentlighet. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineOnArchiveKvittering = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. Du kan ikke opprette tilsynskvitteringen i arkivet før applikasjonen er online. \n' +
    '\n' +
    'Prøv på nytt når du har internettilgang. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineOnSeSammenstilling = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. \n' +
    '\n' +
    'Du kan derfor ikke se sammenstillingene til arkiverte tilsynskvitteringer. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineOnOpenFordelTilKollega = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. \n' +
    '\n' +
    'Du kan derfor ikke fordele tilsynskvitteringer til kollega. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineOnHentFraKollega = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. \n' +
    '\n' +
    'Du kan derfor ikke hente tilsynskvitteringer fra kollega. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineOnHentBilderFraGalleri = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. \n' +
    '\n' +
    'Du kan derfor ikke hente bilder fra galleriet. \n',
  dismissible: true,
  type: 'DANGER',
})

export const offlineOnOpenTidligereKontrollpunkter = (): IToast => ({
  text:
    'Applikasjonen er <b>offline</b>. \n' +
    '\n' +
    'Du kan derfor ikke hente kontrollpunkter fra tidligere tilsynskvittering. \n',
  dismissible: true,
  type: 'DANGER',
})

export const noImagesSelectedForAction = (): IToast => ({
  text: 'Du må velge bilder for å kunne utføre denne handlingen',
  dismissible: true,
  type: 'DANGER',
})

export const alreadyAttachedImagesToObservasjon = (): IToast => ({
  text: 'Du har valgt bilder som er allerede knyttet til en observasjon. Frigjør disse bildene før du forsøker å knytte dem til observasjon',
  dismissible: true,
  type: 'WARN',
})

export const sakCannotBeUsed = (): IToast => ({
  text: 'Saken du har lagt til kan ikke brukes. Du må enten fjerne saken eller legge til en ny sak',
  timeoutSeconds: 5,
  dismissible: true,
  type: 'DANGER',
})

export const tilsynsobjektMissing = (): IToast => ({
  text: 'Du må legge til et tilsynsobjekt før du kan starte tilsynet.',
  timeoutSeconds: 5,
  dismissible: true,
  type: 'DANGER',
})

export const typeTilsynMissing = (): IToast => ({
  text: 'Du må velge type tilsyn før du kan starte tilsynet',
  timeoutSeconds: 5,
  dismissible: true,
  type: 'DANGER',
})

export const typeTilsynBeskrivelseMissing = (): IToast => ({
  text: 'Du må legge til beskrivelse av type tilsyn før du kan starte tilsynet',
  timeoutSeconds: 5,
  dismissible: true,
  type: 'DANGER',
})

export const sakNotAdded = (): IToast => ({
  text: 'Du har lagt inn et saksnummer i søkefeltet uten å søke opp og legge til saken',
  timeoutSeconds: 5,
  dismissible: true,
  type: 'DANGER',
})

export const sammenstillingUOToast = (): IToast => ({
  text: 'Kvitteringen er unntatt offentlighet. Finn evt. kvitteringen i arkivet.',
  timeoutSeconds: 3,
  type: 'WARN',
  dismissible: true,
})

export const deleteKvitteringSuccessToast = (): IToast => ({
  text: 'Tilsynet ble slettet',
  timeoutSeconds: 5,
  dismissible: true,
  type: 'SUCCESS',
})

export const deleteKvitteringFailedToast = (): IToast => ({
  text: 'Kunne ikke slette tilsyn',
  timeoutSeconds: 10,
  dismissible: true,
  type: 'DANGER',
})

export const fordelKvitteringSuccessToast = (navn: string): IToast => ({
  text: `Tilsynskvitteringen ble fordelt til ${navn}`,
  timeoutSeconds: 3,
  dismissible: true,
  type: 'SUCCESS',
})

export const fordelKvitteringFailedToast = (): IToast => ({
  text: 'Kunne ikke fordele tilsynskvitteringen',
  timeoutSeconds: 10,
  dismissible: true,
  type: 'DANGER',
})

export const hentKvitteringerSuccessToast = (kollega: string): IToast => ({
  text: `Hentet tilsynskvitteringer fra ${kollega}`,
  timeoutSeconds: 3,
  dismissible: true,
  type: 'SUCCESS',
})

export const hentKvitteringerFailedToast = (): IToast => ({
  text: `Kunne ikke hente tilsynskvitteringer`,
  timeoutSeconds: 10,
  dismissible: true,
  type: 'DANGER',
})

export const unacceptedImageFormatError = (): IToast => ({
  text: 'Bildet du lastet opp har feil format.',
  timeoutSeconds: 5,
  dismissible: true,
  type: 'DANGER',
})

export const kvitteringArkiveringFailedToast = (): IToast => ({
  text: 'Kunne ikke arkivere tilsyn',
  timeoutSeconds: 3,
  dismissible: true,
  type: 'DANGER',
})

export const kvitteringFerdigstillingFailedToast = (): IToast => ({
  text: 'Kunne ikke ferdigstille tilsyn',
  timeoutSeconds: 3,
  dismissible: true,
  type: 'DANGER',
})
